// Novo arquivo: GeneratorForm.tsx
import React, { useEffect, useRef, useState } from 'react'
import Keyboard from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'
import { ButtonType, GeneratorProps } from '../../common/global'
import InputMultiline from '../Inputs/InputMultiline'
import { ButtonCheck, ButtonContainer, CheckboxContainer, CheckboxContainerRow, CheckboxListContainer, ErrorLabel, Form, FormGroup, Input, Label, ObservationLabel } from './styled'

// ─── Layout numérico para campos phone/numeric ───────────────────────────────
const NUMERIC_LAYOUT = {
  default: [
    '1 2 3',
    '4 5 6',
    '7 8 9',
    '{bksp} 0 {enter}',
  ],
};

const NUMERIC_DISPLAY: Record<string, string> = {
  '{bksp}':  '⌫',
  '{enter}': '✔',
};

// ─── Layout alfabético simplificado (sem TAB, CAPS, SHIFT, .COM) ─────────────
const ALPHA_LAYOUT = {
  default: [
    'Q W E R T Y U I O P',
    'A S D F G H J K L',
    'Z X C V B N M {bksp}',
    '{space} {enter}',
  ],
};

const ALPHA_DISPLAY: Record<string, string> = {
  '{bksp}':  '⌫',
  '{enter}': '✔',
  '{space}': 'ESPAÇO',
};

const isNumericField = (field: any): boolean =>
  Array.isArray(field.format) &&
  (field.format.includes('phone') || field.format.includes('numeric'));

const GeneratorForm: React.FC<GeneratorProps> = React.memo(({ // Utilize o React.memo aqui
  fields,
  formData,
  formErrors, 
  setFormErrors,
  handleInputChange,
  handleFormSubmit,
}) => {
  const [selectedButton, setSelectedButton] = useState<string | null>(null);
  const [inputExtra, setInputExtra] = useState<boolean>(false);
  const [keyboardOpen, setKeyboardOpen] = useState<boolean>(false);
  const keyboardRefs = useRef<{ [key: string]: any }>({});
  const [activeInput, setActiveInput] = useState<string | null>(null);

  const handleInputChangeButton = (value: string | undefined) => {
    setSelectedButton(value || null);
  };

  const handleKeyboardChange = (field: any) => (input: string) => {
    // Numérico: mantém apenas dígitos; texto: sempre maiúsculo
    let processed = isNumericField(field)
      ? input.replace(/[^0-9]/g, '')
      : input.toUpperCase();
    if (field.charCount && processed.length > field.charCount) {
      processed = processed.slice(0, field.charCount);
    }
    handleInputChange({ id: activeInput!, value: processed } as ButtonType, field.format);
  };

  const handleFocus = (inputName: string) => {
    setActiveInput(inputName);
    setKeyboardOpen(true);
  };

  /**
   * Fecha o teclado virtual e absorve o próximo click/touchend que o
   * navegador dispara quando o elemento do teclado some do DOM no meio
   * de um toque. Sem isso o evento "cai" no botão PROSSIGA.
   */
  const closeKeyboardSafely = () => {
    setKeyboardOpen(false);
    const absorb = (e: Event) => { e.stopPropagation(); e.preventDefault(); };
    document.addEventListener('click',    absorb, { capture: true, once: true });
    document.addEventListener('touchend',  absorb, { capture: true, once: true });
    // Garante limpeza caso o evento não ocorra
    setTimeout(() => {
      document.removeEventListener('click',   absorb, { capture: true } as any);
      document.removeEventListener('touchend', absorb, { capture: true } as any);
    }, 400);
  };

  // Close keyboard when tapping outside (empty area)
  useEffect(() => {
    const handleOutsideDown = () => setKeyboardOpen(false);
    document.addEventListener('mousedown', handleOutsideDown);
    document.addEventListener('touchstart', handleOutsideDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideDown);
      document.removeEventListener('touchstart', handleOutsideDown);
    };
  }, []);

  useEffect(() => {
    
  }, [fields, formData])

  // text-line
  return (
    <Form onSubmit={handleFormSubmit}>
      {fields.map((field) => (
        <FormGroup key={field.name}>
          {field.visible && (
            <>
              {/* Renderiza o Label apenas se o tipo não for 'check' */}
              {field.type !== 'check' && (
                <Label htmlFor={field.name} color={field.colorText} fontSize={field.fontSize}>
                  {field.description}
                </Label>
              )}

              {field.type === 'input' && (<>
                <Input
                  invalid={formErrors[field.name] ? true : false}
                  type="text"
                  id={field.name}
                  value={formData[field.name] || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | ButtonType) => handleInputChange(e, field.format)}
                  autoComplete="off"
                  placeholder={field.placeholder}
                  maxLength={field.charCount || 50}
                  style={{ fontSize: `${field.fontSize}px` }}
                  onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                  onTouchStart={(e: React.TouchEvent) => e.stopPropagation()}
                  onFocus={() => handleFocus(field.name)}
                  onBlur={(e: { target: { value: string } }) => {
                    if (e.target.value === '' && field.mandatory) {
                      setFormErrors((prevData: any) => ({
                        ...prevData,
                        [field.name]: 'Preenchimento obrigatório!',
                      }))
                    }
                  }}
                />
                
                {keyboardOpen && activeInput === field.name && (
                  <div style={{ width: '100%' }} className={isNumericField(field) ? 'kb-numeric-wrapper' : ''} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
                    {isNumericField(field) ? (
                      <Keyboard
                        onChange={() => { /* controlado por onKeyPress */ }}
                        keyboardRef={(r) => (keyboardRefs.current[field.name] = r)}
                        inputName={field.name}
                        layout={NUMERIC_LAYOUT}
                        layoutName="default"
                        display={NUMERIC_DISPLAY}
                        theme="hg-theme-default hg-layout-numeric"
                        onKeyPress={(btn: string) => {
                          if (btn === '{enter}') {
                            closeKeyboardSafely();
                            return;
                          }
                          const current = String(formData[activeInput!] || '');
                          let newVal: string;
                          if (btn === '{bksp}') {
                            newVal = current.slice(0, -1);
                          } else {
                            newVal = current + btn;
                          }
                          handleInputChange({ id: activeInput!, value: newVal } as ButtonType, field.format);
                          // Sincroniza estado interno do teclado com dígitos puros
                          const rawDigits = newVal.replace(/\D/g, '');
                          keyboardRefs.current[field.name]?.setInput(rawDigits);
                        }}
                      />
                    ) : (
                      <Keyboard
                        onChange={handleKeyboardChange(field)}
                        keyboardRef={(r) => (keyboardRefs.current[field.name] = r)}
                        inputName={field.name}
                        layout={ALPHA_LAYOUT}
                        layoutName="default"
                        display={ALPHA_DISPLAY}
                        onKeyPress={(btn: string) => {
                          if (btn === '{enter}') closeKeyboardSafely();
                        }}
                      />
                    )}
                  </div>
                )}
              </>)}
              {field.type === 'select' && (
                <select //Aqui, alteramos para a tag <select>
                  id={field.name}
                  value={formData[field.name] || ''}
                  onChange={handleInputChange}
                  style={{ fontSize: `${field.fontSize}px` }}
                  onBlur={(e: { target: { value: string } }) => {
                    if (e.target.value === '' && field.mandatory) {
                      setFormErrors((prevData: any) => ({
                        ...prevData,
                        [field.name]: 'Preenchimento obrigatório!',
                      }))
                    }
                  }}
                >
                  <option value="">Selecione uma opção</option>
                  {field.options?.map((option, index) => (
                    <option key={index} value={option.optionValue}> {/* Alteramos para 'option.optionName' */}
                      {option.optionValue}
                    </option>
                  ))}
                </select>
              )}
              {field.type === 'multiline' && (
                <div>
                  <InputMultiline 
                    id={field.name}    
                    value={formData[field.name] || ''}
                    onChange={handleInputChange} maxRows={5}
                    fontSize={field.fontSize}
                    placeholder={field.placeholder}
                    onBlur={(e: { target: { value: string } }) => {
                    if (e.target.value === '' && field.mandatory) {
                      setFormErrors((prevData: any) => ({
                        ...prevData,
                        [field.name]: 'Preenchimento obrigatório!',
                      }))
                    }
                  }}
                  />
                </div>
              )}
              {field.type === 'check-list' && (
                <CheckboxListContainer>
                  {field.options?.map((option) => (
                    <CheckboxContainer key={option.value} color={field.colorText} fontSize={option.fontSize || 20}>
                      <Input
                        type="checkbox"
                        id={field.name}
                        value={formData[field.name] || ''}
                        onClick={(e: any) => {
                          if (formData[field.name] && formData[field.name] !== '') {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e: { preventDefault: () => void }) => {
                          if (formData[field.name] === '' || !formData[field.name]) {
                            if (option?.input) {
                              setInputExtra(true);
                            }
                            handleInputChange({ id: field.name, value: option.value } as ButtonType);
                          } else {
                            e.preventDefault();
                          }
                        }}
                        autoComplete="off"
                        onBlur={(e: any) => {
                          if (formData[field.name] === '' || !formData[field.name]) {
                            if (!e.target.checked && field.mandatory) {
                              setFormErrors((prevData: any) => ({
                                ...prevData,
                                [field.name]: 'Preenchimento obrigatório!',
                              }));
                            }
                          }
                        }}
                      />
                      {option?.value || ''}
                      {option?.input && inputExtra && (
                        <>
                          {' '}
                          {' '}
                          <label>{option['input-value'] || ' '}</label>
                          <Input
                            type="input"
                            id={field.name}
                            value={formData[field.name] || ''}
                            onChange={(e: { target: { value: string } }) =>
                              handleInputChange({
                                id: field.name,
                                value: e.target.value === '' ? ' ' : e.target.value,
                              } as ButtonType)
                            }
                            autoComplete="off"
                            style={{ width: '350px', marginLeft: '50px', fontSize: `${field.fontSize}px` }}
                            onBlur={(e: any) => {
                              if (e.target.value === '' && field.mandatory) {
                                setFormErrors((prevData: any) => ({
                                  ...prevData,
                                  [field.name]: 'Preenchimento obrigatório!',
                                }));
                              }
                            }}
                          />
                        </>
                      )}
                    </CheckboxContainer>
                  ))}
                </CheckboxListContainer>
              )}

              {field.type === 'check-button' && (<ButtonContainer color={field.colorText} fontSize={field.fontSize || 20}>
                {field.options?.map(option => (
                  <ButtonCheck
                    color={""}
                    fontSize={field.fontSize || 20}
                    id={field.name}
                    isSelected={option.value === selectedButton && formData[field.name]}
                    onClick={() => {
                      handleInputChangeButton(option.value)
                      handleInputChange({ id: field.name, value: option.value} as ButtonType)
                      if (option.value === '' && field.mandatory) {
                        setFormErrors((prevData: any) => ({
                          ...prevData,
                          [field.name]: 'Preenchimento obrigatório!',
                        }))
                      }
                    }}
                  >{option.value}</ButtonCheck>
                ))}                
              </ButtonContainer>)}
              {field.type === 'check' && (
                <CheckboxContainerRow>
                  <Label
                    htmlFor={field.name}
                    color={field.colorText}
                    fontSize={field.fontSize}
                    style={{ marginRight: '10px', marginTop: '5px' }}
                  >
                    {field.description}
                  </Label>
                  <Input
                    type="checkbox"
                    id={field.name}
                    checked={formData[field.name] || false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange({ id: field.name, value: e.target.checked } as unknown as ButtonType)
                    }
                    style={{ width: '40px', height: '40px' }} // Ajuste o tamanho conforme necessário
                  />
                </CheckboxContainerRow>
              )}

              {/* Observações */}
              {field.observation && <ObservationLabel>{field.observation}</ObservationLabel>}
              {/* Outros tipos de campos aqui */}
              {formErrors[field.name] && <ErrorLabel>{formErrors[field.name]}</ErrorLabel>}
            </>
          )}
        </FormGroup>
      ))}
      {/* <Button type="submit">INICIAR</Button> */}
    </Form>
  );
});

export default GeneratorForm;
